import { faStar } from "@jewlr/pro-regular-svg-icons/faStar"
import { faStar as solidStar } from "@jewlr/pro-solid-svg-icons/faStar"
import { faStarHalfAlt as solidStarHalf } from "@jewlr/pro-solid-svg-icons/faStarHalfAlt"
import PropTypes from "prop-types"
import { useEffect, useState } from "react"
import { Link } from "react-router-dom"

import Badge from "areas/collection/badge"
import LooksSelector from "areas/collection/looks-selector"
import ProductDescription from "areas/collection/products/product-description"
import ProductPrice from "areas/collection/products/product-price"
import FontAwesomeIcon from "components/ui/font-awesome-icon"
import { IS_JEWLR } from "helpers/application"
import { roundToHalf } from "helpers/round-to-half"

import ProductImage from "./product-image"

const setDefaultLook = (product) => {
  if (!product.looks) return ""
  return product.looks && product.default_look
    ? product.default_look
    : Object.keys(product.looks)[0]
}

const ProductCard = ({
  element,
  handleClick,
  homepageExp,
  imageSizes,
  index,
  product,
}) => {
  const [selectedLook, setSelectedLook] = useState(setDefaultLook(product))

  const selectedProductLook = product.looks ? product.looks[selectedLook] : null

  if (!selectedProductLook) {
    // Prevents selectedLook from being undefined and breaking the component on reload of products. Gives useEffect a chance to update.
    return null
  }

  const badge = product.badge
  const showRatings =
    product.reviews &&
    product.reviews.count > 0 &&
    product.reviews.average_rating >= 4

  const onItemClick = () => {
    handleClick(product.title || product.name, selectedProductLook.product_url)
  }

  useEffect(() => {
    // Update selected metal when default_look is changed
    setSelectedLook(setDefaultLook(product))
  }, [product])

  return (
    <div>
      <Link
        className="relative mb-8 block"
        data-lc={element.anchor_name || element.id}
        onClick={onItemClick}
        to={selectedProductLook.product_url}
      >
        {!homepageExp && badge && (
          <Badge label={badge.label} variant={badge.variant} />
        )}
        <ProductImage
          alt={selectedProductLook.alt || product.name}
          images={selectedProductLook.images}
          index={index}
          sizes={
            imageSizes ||
            "(max-width: 767px) 50vw, (max-width: 1023px) 23vw, (max-width: 1920px) calc((100vw - 320px) / 4.5), 322px"
          }
          styleCode={product.style_code}
          type={product.product_type}
        />
      </Link>
      {homepageExp && (
        <LooksSelector
          className="desktop:pt-12 desktop:pb-16 justify-start py-6"
          looks={product.looks}
          product={product}
          selectedLook={selectedLook}
          updateLook={(look) => setSelectedLook(look)}
        />
      )}
      {IS_JEWLR() && !homepageExp && (
        <div
          className="text-12 desktop:my-20 mb-12 flex h-12 items-center gap-8"
          {...(showRatings && {
            "aria-label": `Rated ${product.reviews.average_rating.toFixed(1)} out of 5 from ${product.reviews.count} reviews`,
          })}
        >
          {showRatings && (
            <>
              <span className="text-rating flex gap-2">
                {(() => {
                  const average_rating = roundToHalf(
                    product.reviews.average_rating,
                    0.5
                  )
                  const starArray = []

                  for (let i = 1; i <= 5; i++) {
                    if (i <= average_rating) {
                      starArray.push(
                        <FontAwesomeIcon icon={solidStar} key={i} />
                      )
                    } else if (i <= average_rating + 0.5) {
                      starArray.push(
                        <FontAwesomeIcon icon={solidStarHalf} key={i} />
                      )
                    } else {
                      starArray.push(<FontAwesomeIcon icon={faStar} key={i} />)
                    }
                  }

                  return starArray
                })()}
              </span>
              <span className="text-12 leading-12 font-semibold">
                {product.reviews.count} Reviews
              </span>
            </>
          )}
        </div>
      )}
      <ProductDescription
        homepageExp={homepageExp}
        productName={product.title || product.name}
      />
      {selectedProductLook.selling && (
        <ProductPrice
          homepageExp={homepageExp}
          product={{
            ...selectedProductLook,
            style_code: product.style_code,
          }}
        />
      )}
      {product.hasDiamond && (
        <span className="text-13 desktop:text-16 italic">
          {product.hasDiamond}
        </span>
      )}
      {!homepageExp && (
        <LooksSelector
          className="desktop:pt-12 justify-start pb-0 pt-8"
          looks={product.looks}
          product={product}
          selectedLook={selectedLook}
          updateLook={(look) => setSelectedLook(look)}
        />
      )}
    </div>
  )
}

ProductCard.propTypes = {
  element: PropTypes.object,
  handleClick: PropTypes.func,
  homepageExp: PropTypes.bool,
  imageSizes: PropTypes.string,
  index: PropTypes.number,
  product: PropTypes.object,
}

export default ProductCard
